import React, { createContext, useContext, useRef } from 'react';

import { useAppSelector } from '../../../../../../../app/hooks';
import { AgentInput, Message, ReasoningStep } from '../../../../../../../store/dashboard/askAica/actions';
import { ConversationListItem } from '../../../../../../../store/dashboard/askAica/types';
import { CurrentCompany, AskBackGate, DeclineGate } from '../../../../../../../store/globalAskAica/types';
import { PickedSlot } from '../../../../../../../store/globalAskAica/askBackSlots';
import type { WhatsAppSurface } from '../../../../../../../store/globalAskAica/whatsapp';
import { deriveNewestTurn, getPendingClarification, selectViewOnly } from '../../../../../../../store/dashboard/askAica/selectors';
import { ClarificationAnswer, ClarificationCard } from '../../../../../../../store/dashboard/askAica/clarificationTypes';

/**
 * The two AskAicaV2 surfaces — a single source of truth for the 'leads'/'portfolio'
 * string values so they're never hand-typed inline (human review #14). The VALUES
 * themselves are a frozen contract (backend `source` field, tests) — never change them,
 * only where they're referenced from.
 */
export const ASK_AICA_SURFACE = {
    LEADS: 'leads',
    PORTFOLIO: 'portfolio',
} as const;

export type AskAicaSurface = (typeof ASK_AICA_SURFACE)[keyof typeof ASK_AICA_SURFACE];

/**
 * The "active Ask AICA data + actions" surface shared by the AskAicaV2 leaf
 * components (MessagesContainer, ReasoningPanel, AskAicaInputBox, LeftPanel,
 * AskAicaV2 itself) — RECUR-27369 Option B (Surface Context).
 *
 * Two producers, both mapping onto this SAME shape:
 *  - LeadsAskAicaSurfaceProvider — thin passthrough of useAskAica() + the leads
 *    askAica slice selectors. Data path stays byte-identical to today.
 *  - PortfolioAskAicaSurfaceProvider — thin passthrough of useGlobalAskAica().
 *
 * Fields prefixed "portfolio-only" are undefined on the leads surface.
 */
export interface AskAicaSurfaceContextValue {
    surface: AskAicaSurface;

    // GlobalMessage (portfolio) is structurally assignable — id/type/content/
    // timestamp match, and every other Message field is optional.
    messages: Message[];
    reasoning?: ReasoningStep;
    isLoading: boolean;
    isLoadingChatHistory: boolean;
    error: string | null;
    hasMoreData: boolean;

    selectedSources: string[];

    /** Both surfaces (RECUR-28136) — the newest turn is still being produced
     * server-side, as read off its assistant row's status. True on a reloaded
     * page, in a second tab, and after a Stop until the row settles; FALSE for a
     * live turn in this tab, which `isLoading` already covers. */
    hasInProgressTurn?: boolean;
    /** Both surfaces — stops the newest turn's run server-side. A client
     * disconnect no longer cancels anything, so this is what Stop must call.
     * Resolves true once the server accepts the stop (the caller must leave the
     * stream open for Athena's synthetic final frame rather than aborting it),
     * or false if the request itself failed (the caller falls back to a local
     * abort — the run may not have stopped at all). See useAskAica.ts::stopRun
     * for the full contract, including its bounded local-settle fallback. */
    stopRun?: () => Promise<boolean>;
    /** Both surfaces — the newest assistant message is a clarifying-question round still waiting
     * on the user. Gates every entry point that would start a turn (the composer, suggestions,
     * the scope-line pin, the company chip/picker); Stop stays live. */
    hasPendingClarification?: boolean;
    /** Both surfaces — the pending round itself, for the docked card above the composer. */
    pendingClarification?: ClarificationCard;
    /** Both surfaces — sends the round's answers (one per question) as ONE turn. */
    answerClarification?: (card: ClarificationCard, answers: ClarificationAnswer[]) => (() => void) | undefined;
    /** Both surfaces — reconnecting to the run hit its wall-clock bound. NOT an
     * error: it only switches the working-state copy. */
    reattachExhausted?: boolean;

    sendMessage: (content: string, flowKey?: string, agentInput?: AgentInput) => (() => void) | undefined;
    clearChat: () => void;
    fetchChatHistory: (isInitialFetch?: boolean, conversationId?: string | null, reset?: boolean) => void;
    fetchOrgConversations: () => void;
    /** Leads only — Library file listing has no portfolio equivalent. */
    fetchLibraryFiles?: (orgId: string) => void;
    registerStreamStartedCallback: (callback: (value: boolean) => void) => void;
    messagesEndRef: React.RefObject<HTMLDivElement>;

    /** File-download / add-to-reports context org: leads = route org id,
     * portfolio = the gate-resolved currentCompany.org_id. */
    fileContextOrgId?: string;

    // GlobalConversationListItem (portfolio) has the identical shape.
    conversations: ConversationListItem[];
    isLoadingConversations: boolean;
    /** Leads: the sidebar's selectedConversationId. Portfolio: conversationId
     * (the in-progress/loaded conversation — may be set even before any sidebar
     * selection, e.g. once a live turn returns its first conversation_id). */
    selectedConversationId?: string | null;
    isClearingChat: boolean;
    isViewOnly: boolean;

    /** Leads-only: the org currently driving the chat (org-switch clear guard). */
    activeOrgId?: string | null;
    /** Leads-only: @chuckNorris feedback-mention mode. Always false on portfolio. */
    enableFeedbackMention: boolean;

    /** Both surfaces (Conversations v2) — resets the view and arms the next send
     * to force-create a new conversation (D1/D2); never creates a row itself. */
    newChat?: () => void;
    /** Both surfaces — deletes a specific conversation by id, branching active
     * (resets the view) vs background (leaves it untouched) (D3). */
    deleteConversation?: (conversationId: string) => void;
    /** Both surfaces — true from New Chat click until the next turn's first
     * conversation_id lands (D1/D2). */
    pendingNewConversation?: boolean;
    /** Both surfaces — switches the WHOLE panel to a specific conversation, the
     * same mechanism ConversationSidebar's row click uses (leads:
     * resetChatView+setSelectedConversation+fetchChatHistory; portfolio:
     * loadConversation). Used by a cross-chat citation click ("Related
     * Conversations") to navigate in place rather than opening a separate
     * viewer. Undefined on a surface with no switch-conversation mechanism
     * wired — callers must no-op gracefully rather than assume it exists. */
    openConversation?: (conversationId: string, targetMessageId?: string | null) => void;
    /** Leads only, so far — true when the currently-loaded conversation
     * (selectedConversationId) was soft-deleted server-side. Sourced from the
     * chat-history response's own `is_deleted` field (Athena's share-view
     * contract) — NOT inferred from absence in the `conversations` list, since
     * a citation-click navigation target is often not in that list at all. */
    selectedConversationIsDeleted?: boolean;
    /** The message a cross-chat citation click wants focused once its target
     * conversation finishes loading — set by openConversation's second arg,
     * consumed by MessagesContainer (scroll + highlight), then cleared via
     * clearFocusMessageId. Undefined/null when no navigation is pending. */
    focusMessageId?: string | null;
    clearFocusMessageId?: () => void;

    // ── Portfolio-only gate extras (undefined on leads) ──────────────────────
    currentCompany?: CurrentCompany | null;
    companyLess?: boolean;
    askBack?: AskBackGate | null;
    decline?: DeclineGate | null;
    /** Confirming the picker form — ONE atomic payload carrying every ambiguous
     * slot's choice (RECUR-27889). Was a single-candidate row tap before. */
    replyToGate?: (picked: PickedSlot[]) => void;
    dismissAskBack?: () => void;
    clearCompany?: () => void;
    /** Pins a company from an answer's scope chip (RECUR-27889) — sends
     * gate_action:{switch}. Undefined on leads, which renders no scope line. */
    pinCompany?: (orgId: string) => void;
    loadConversation?: (conversationId: string) => void;
    /** Fetches the conversation list for the sidebar — called once when the surface
     * mounts. Does NOT auto-load any conversation — the Portfolio orb always opens
     * to a fresh chat, on both desktop and mobile. Leads has no equivalent here;
     * its own fetchChatHistory/fetchOrgConversations calls on open still
     * auto-resume the most recent conversation there. */
    openChat?: () => void;
    /** Portfolio only: the user's WhatsApp chats; undefined on leads or when switched off. */
    whatsApp?: WhatsAppSurface;
}

/**
 * The in-progress-turn quartet (RECUR-28136; hasPendingClarification added for
 * clarifying questions) — hasInProgressTurn/stopRun/reattachExhausted/
 * hasPendingClarification must travel TOGETHER to any surface that renders shared
 * AskAicaV2 leaves without an AskAicaSurfaceProvider mounted (the mobile
 * screen — see useMobileAskAicaLogic.ts / MobileAskAicaScreen.tsx). Keys are
 * required (not optional) so a consumer that forgets to forward one is a
 * compile error there, rather than a silently-undefined field at runtime —
 * exactly the failure mode that dropped reattachExhausted from mobile.
 */
export interface InProgressTurnState {
    hasInProgressTurn: boolean | undefined;
    stopRun: (() => Promise<boolean>) | undefined;
    reattachExhausted: boolean | undefined;
    hasPendingClarification: boolean | undefined;
}

const AskAicaSurfaceContext = createContext<AskAicaSurfaceContextValue | undefined>(undefined);

export const AskAicaSurfaceProvider = AskAicaSurfaceContext.Provider;

/**
 * Maps the raw leads `askAicaReducer` slice onto the surface context's
 * data-only fields (everything except actions/refs) — the SAME field names
 * and defaults LeadsAskAicaSurfaceProvider derives from its own selectors, so
 * the "leads surface shape" is defined in one place instead of being
 * hand-duplicated in both the real provider and this defensive fallback.
 * Every read is optional-chained: callers may pass a partial/undefined slice
 * (e.g. an isolated test mocking only the fields it cares about).
 */
export const mapLeadsSliceToSurfaceData = (askAica: any) => {
    const pendingClarification = getPendingClarification(askAica?.messages?.[0]);
    return {
        surface: ASK_AICA_SURFACE.LEADS,
        messages: askAica?.messages ?? [],
        reasoning: askAica?.reasoning,
        isLoading: askAica?.isLoading ?? false,
        isLoadingChatHistory: askAica?.isLoadingChatHistory ?? false,
        error: askAica?.error ?? null,
        hasMoreData: askAica?.pagination?.hasMoreData ?? false,
        selectedSources: askAica?.uiState?.selectedSources ?? [],
        conversations: askAica?.conversations ?? [],
        isLoadingConversations: askAica?.isLoadingConversations ?? false,
        selectedConversationId: askAica?.selectedConversationId ?? null,
        isClearingChat: askAica?.isClearingChat ?? false,
        activeOrgId: askAica?.activeOrgId ?? null,
        pendingNewConversation: askAica?.pendingNewConversation ?? false,
        // Leads messages are newest-first, so the newest turn is the leading pair.
        // Derived here too (not only in the provider) so the mobile surface — which
        // mounts this leaf without a provider — still blocks its composer while a run
        // is in flight. Stopping that run needs the hook, so it stays provider-only.
        hasInProgressTurn: deriveNewestTurn(askAica?.messages?.[0], askAica?.messages?.[1]).isInProgress,
        // Derived for the same reason: the docked card and the entry-point gates must work on the
        // provider-less mobile surface. Answering it needs the hook, so that stays an explicit prop.
        pendingClarification,
        hasPendingClarification: !!pendingClarification,
    };
};

/**
 * Bare-minimum, defensive read of the leads askAica slice. Used by isolated
 * component tests that render a shared leaf directly with no provider mounted
 * (e.g. MessagesContainer.test.tsx), AND — deliberately, in production — by
 * the mobile Ask AICA screen, which renders AskAicaInputBox/MessagesContainer
 * with no AskAicaSurfaceProvider (RECUR-28136). This fallback never carries
 * real actions: any action mobile actually needs (stopRun, hasInProgressTurn)
 * is passed to those components as an explicit prop instead of relying on
 * this fallback — see AskAicaInputBox's stopRun/hasInProgressTurn props.
 */
const useNoProviderFallback = (): AskAicaSurfaceContextValue => {
    const askAica = useAppSelector((state: any) => state?.dashboard?.askAicaReducer);
    // Read-only share viewers must see a pending round as a note, on the provider-less mobile screen too.
    const isViewOnly = useAppSelector((state: any) => (state?.dashboard?.askAicaReducer ? selectViewOnly(state) : false));
    const messagesEndRef = useRef<HTMLDivElement>(null);

    return {
        ...mapLeadsSliceToSurfaceData(askAica),
        sendMessage: () => undefined,
        clearChat: () => {},
        newChat: () => {},
        deleteConversation: () => {},
        fetchChatHistory: () => {},
        openConversation: () => {},
        fetchOrgConversations: () => {},
        fetchLibraryFiles: undefined,
        registerStreamStartedCallback: () => {},
        messagesEndRef,
        fileContextOrgId: undefined,
        isViewOnly,
        enableFeedbackMention: true,
    };
};

/**
 * Reads the active surface's data + actions. Falls back to a bare leads-slice
 * read (see useNoProviderFallback) when no provider is mounted — this is what
 * keeps every pre-existing leads jest suite green without a wrapper (§4 of the
 * plan). Both hooks are always called (never conditionally) so this stays
 * rules-of-hooks safe; only the RESULT is chosen based on provider presence.
 */
export const useAskAicaSurface = (): AskAicaSurfaceContextValue => {
    const ctx = useContext(AskAicaSurfaceContext);
    const fallback = useNoProviderFallback();
    return ctx ?? fallback;
};

export default AskAicaSurfaceContext;
